import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, ArrowLeftLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {pulseUserAtom} from 'atoms/auth';
import {newsPostIdAtom} from 'atoms/news';
import {NewsPost} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {DSYS_RAIL_INSET_LEFT_PX, DSYS_RAIL_INSET_RIGHT_PX} from 'components/DSYS/layoutConstants';
import {ButtonLink} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {NewsEmptyState} from 'components/news/NewsEmptyState';
import {NewsList} from 'components/news/NewsList';
import {isContentEditor} from 'helpers/utils';
import {useCondensedNews} from 'hooks/isMobile';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtom, useAtomValue} from 'jotai';
import {useEffect} from 'react';
import {useLoaderData, useLocation, useNavigate, useParams} from 'react-router';

import {NewsPostRight} from './NewsPostRight';
import styles from './NewsView.module.css';

// The feed's rows bleed back across Layout.Page's rail insets, so fills run edge to edge.
const RAIL_BLEED = {left: DSYS_RAIL_INSET_LEFT_PX, right: DSYS_RAIL_INSET_RIGHT_PX};

export const NewsView = () => {
  const [latestPosts, newsPost] = useLoaderData() as [NewsPost[], NewsPost | undefined];
  const params = useParams();
  const [loadedNewsPostId, setLoadedNewsPostId] = useAtom(newsPostIdAtom);
  const categoryInUrl = params.identifier && !/^\d+$/.test(params.identifier);
  const isMobile = useCondensedNews();
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const isEditorView = pulseUser && isContentEditor(pulseUser);

  useDocumentTitle(categoryInUrl ? (params.identifier ?? t('news')) : t('news'));

  useEffect(() => {
    if (params.identifier && /^\d+$/.test(params.identifier)) {
      const id = parseInt(params.identifier, 10);
      setLoadedNewsPostId(id);
    }
  }, [params.identifier, setLoadedNewsPostId]);

  useEffect(() => {
    analyticsLogger().logEvent('NEWS_LOADED', {source: location.state?.source});
  }, [location.state?.source]);

  useEffect(() => {
    if (categoryInUrl) {
      analyticsLogger().logEvent('NEWS_VIEWED', {type: 'category'});
    } else if (params.identifier && /^\d+$/.test(params.identifier)) {
      analyticsLogger().logEvent('NEWS_VIEWED', {type: 'post'});
    } else if (!params.identifier) {
      analyticsLogger().logEvent('NEWS_VIEWED', {type: 'home'});
    }
  }, [categoryInUrl, params.identifier]);

  if (!newsPost && latestPosts.length === 0) {
    return <NewsEmptyState isEditorView />;
  }

  const categoryName = latestPosts[0].category.find(
    (category) => category.name.toLowerCase() === params.identifier?.toLowerCase()
  )?.name;

  if (isMobile) {
    return !params.identifier || categoryInUrl ? (
      <Layout.Container>
        <div className={styles.newsFeed}>
          <Title
            className={styles.title}
            size={24}
            withAccessoryEnd={
              categoryInUrl && (
                <Chip
                  aria-label={params.identifier + '- press backspace or delete to remove'}
                  size="small"
                  onDelete={() => {
                    navigate('/news', {state: {source: 'news-category-chip'}});
                  }}
                >
                  <Chip.Content>{categoryName}</Chip.Content>
                </Chip>
              )
            }
          >
            {t('news')}
          </Title>
          <NewsList
            isEditorView={isEditorView}
            viewCategory={categoryInUrl ? categoryName : ''}
            selected={loadedNewsPostId}
          />
        </div>
      </Layout.Container>
    ) : (
      <Layout.Container>
        <div className={styles.mobileActions}>
          <ButtonLink
            to="/news"
            variant="borderless"
            withIconStart={<UIIcon src={ArrowLeftLine}></UIIcon>}
          >
            {t('all_news')}
          </ButtonLink>
          {/* Edit is on the post's title row. */}
          {isEditorView && (
            <div className={styles.editorActions}>
              <ButtonLink
                onClick={() => analyticsLogger().logEvent('NEWS_ADD_POST_CLICKED')}
                to="/news/new"
                variant="primary"
              >
                {t('new_post')}
              </ButtonLink>
            </div>
          )}
        </div>

        <div className={styles.mobileNewsBody}>
          <NewsPostRight
            id={loadedNewsPostId ?? (newsPost?.id ? newsPost.id : latestPosts[0].id)}
          />
        </div>
      </Layout.Container>
    );
  }

  // The header's create button, like Workstreams' and Teams'. Editors only; Edit
  // sits on the open post's title row.
  const editorActions = isEditorView && (
    <Tooltip title={t('new_post')}>
      <IconButton
        variant="transparent"
        size="small"
        aria-label={t('new_post')}
        onClick={() => {
          analyticsLogger().logEvent('NEWS_ADD_POST_CLICKED');
          navigate('/news/new');
        }}
      >
        <UIIcon src={AddLine} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Layout.Page
      header={
        <Layout.PageHeader
          // The title is home: the whole feed, no category.
          title={t('news')}
          titleTo="/news"
          titleAccessory={
            categoryInUrl && (
              <Chip
                aria-label={params.identifier + '- press backspace or delete to remove'}
                size="small"
                onDelete={() => {
                  navigate('/news', {state: {source: 'news-category-chip-delete'}});
                }}
              >
                <Chip.Content>{categoryName}</Chip.Content>
              </Chip>
            )
          }
          create={editorActions || undefined}
        />
      }
      // Thumbnails + titles need the wider rail.
      railSize="feed"
      rail={
        <NewsList
          isEditorView={isEditorView}
          viewCategory={categoryInUrl ? categoryName : ''}
          selected={loadedNewsPostId}
          bleed={RAIL_BLEED}
        />
      }
    >
      <Box paddingTop="24" style={{flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto'}}>
        {/* Editorial: on wide panes the post leans toward the middle. */}
        <Layout.MaxWidth width="reading" inset lean>
          <div className={styles.newsBody}>
            <NewsPostRight
              id={loadedNewsPostId ?? (newsPost?.id ? newsPost.id : latestPosts[0].id)}
            />
          </div>
        </Layout.MaxWidth>
      </Box>
    </Layout.Page>
  );
};
